import { useState, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Pencil, X, Lock } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import PhotoGallery from "./PhotoGallery";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/components/auth/AuthProvider";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

interface Album {
  name: string;
  photos: string[];
}

interface ProjectImagesProps {
  projectId: string;
  initialPhotos?: string[];
  canEdit?: boolean;
  isPublicView?: boolean;
}

const ALBUM_SUGGESTIONS = [
  "Scout Photos",
  "Location References", 
  "Mood Board",
  "Lighting References",
  "Camera References",
  "Set Design",
  "Wardrobe",
  "Custom Album"
];

export default function ProjectImages({ 
  projectId, 
  initialPhotos = [],
  canEdit = false,
  isPublicView = false
}: ProjectImagesProps) {
  const { t } = useTranslation();
  const [albums, setAlbums] = useState<Album[]>([]);
  const [isAddingAlbum, setIsAddingAlbum] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState("");
  const [editingAlbumIndex, setEditingAlbumIndex] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: profile } = useQuery({
    queryKey: ["profile-subscription", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from("profiles")
        .select("subscription_tier")
        .eq("id", user.id)
        .maybeSingle();

      if (error) {
        console.error("Error fetching subscription tier:", error);
        return null;
      }
      
      return data;
    },
    enabled: !!user,
  });

  const isFreeTier = profile?.subscription_tier === 'free';

  if (isFreeTier) {
    return (
      <div className="max-w-4xl mx-auto">
        <Card className="bg-white/5 border-white/10">
          <CardHeader>
            <CardTitle className="text-lg text-white">{t('project_images.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border-2 border-dashed rounded-lg border-white/20 p-16 flex flex-col items-center justify-center">
              <div className="text-center">
                <div className="bg-white/5 p-6 rounded-full mb-4 inline-flex">
                  <Lock className="h-12 w-12 text-amber-500" />
                </div>
                <h3 className="text-xl text-white mb-2">{t('project_images.premium_feature_title')}</h3>
                <p className="text-white/60 max-w-xs mx-auto mb-8">
                  {t('project_images.premium_feature_desc')}
                </p>
                <Button 
                  size="lg"
                  onClick={() => {
                    navigate("/account");
                    toast.info(t('project_images.upgrade_to_upload'));
                  }}
                  className="px-8 py-6 text-lg h-auto"
                >
                  {t('project_images.upgrade_now')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  useEffect(() => {
    const initializeAlbums = async () => {
      try {
        const { data: project } = await supabase
          .from('projects')
          .select('scout_photos, custom_categories')
          .eq('id', projectId)
          .single();

        if (project) {
          const initialAlbums: Album[] = [];
          
          const scoutPhotos = Array.isArray(project.scout_photos) ? project.scout_photos : [];
          if (scoutPhotos.length > 0) {
            initialAlbums.push({ name: "Scout Photos", photos: scoutPhotos as string[] });
          }

          const customCategories = Array.isArray(project.custom_categories) ? project.custom_categories : [];
          customCategories.forEach((albumName: string) => {
            initialAlbums.push({ name: albumName, photos: [] });
          });

          setAlbums(initialAlbums);
        }
      } catch (error) {
        console.error('Error fetching albums:', error);
        toast.error("Failed to load albums");
      }
    };

    initializeAlbums();
  }, [projectId]);

  const updateProjectAlbums = async (newAlbums: Album[]) => {
    setIsSaving(true);
    try {
      const customCategories = newAlbums.map(album => album.name).filter(name => name !== "Scout Photos");
      const scoutPhotos = newAlbums.find(album => album.name === "Scout Photos")?.photos || [];

      const { error } = await supabase.rpc(
        'update_project_details',
        {
          id: projectId,
          custom_categories: customCategories,
          scout_photos: scoutPhotos
        }
      );

      if (error) {
        console.error('Error updating albums:', error);
        throw error;
      }

      await queryClient.invalidateQueries({ queryKey: ["project", projectId] });
      toast.success("Changes saved successfully");
    } catch (error: any) {
      console.error('Error updating albums:', error);
      toast.error("Failed to save changes");
      throw error;
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAlbum = async () => {
    if (!newAlbumName.trim()) {
      toast.error(t('project_images.enter_album_name'));
      return;
    }
    
    try {
      const newAlbums = [...albums, { name: newAlbumName, photos: [] }];
      setAlbums(newAlbums);
      await updateProjectAlbums(newAlbums);
      setNewAlbumName("");
      setIsAddingAlbum(false);
    } catch (error) {
      setAlbums(albums);
    }
  };

  const handleRenameAlbum = async (index: number) => {
    if (!newAlbumName.trim()) {
      toast.error(t('project_images.enter_album_name'));
      return;
    }

    const previousAlbums = [...albums];
    try {
      const newAlbums = [...albums];
      newAlbums[index].name = newAlbumName;
      setAlbums(newAlbums);
      await updateProjectAlbums(newAlbums);
      setNewAlbumName("");
      setEditingAlbumIndex(null);
    } catch (error) {
      setAlbums(previousAlbums);
    }
  };

  const handleDeleteAlbum = async (index: number) => {
    const previousAlbums = [...albums];
    try {
      const newAlbums = albums.filter((_, i) => i !== index);
      setAlbums(newAlbums);
      await updateProjectAlbums(newAlbums);
    } catch (error) {
      setAlbums(previousAlbums);
      toast.error(t('project_images.failed_delete_album'));
    }
  };

  const handleUpgradeClick = () => {
    navigate("/account");
    toast.info("Upgrade to Pro or Producer to upload photos");
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>, albumIndex: number) => {
    if (!canEdit) {
      toast.error(t('project_images.no_permission'));
      return;
    }

    if (isFreeTier) {
      toast.error("Photo uploads are available on Pro and Producer plans");
      handleUpgradeClick();
      return;
    }

    const file = event.target.files?.[0];
    if (!file) return;

    setIsSaving(true);
    try {
      const fileExt = file.name.split(".").pop();
      const filePath = `${projectId}/${crypto.randomUUID()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("project-photos")
        .upload(filePath, file, { metadata: { size: file.size } });

      if (uploadError) {
        toast.error(t('project_images.failed_upload'));
        return;
      }

      const { data: { publicUrl } } = supabase.storage
        .from("project-photos")
        .getPublicUrl(filePath);

      const newAlbums = [...albums];
      newAlbums[albumIndex].photos.push(publicUrl);
      await updateProjectAlbums(newAlbums);
      setAlbums(newAlbums);
      toast.success(t('project_images.upload_success'));
    } catch (error) {
      toast.error(t('project_images.upload_error'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePhoto = async (albumIndex: number, photoUrl: string) => {
    if (!canEdit) {
      toast.error(t('project_images.no_permission'));
      return;
    }

    const previousAlbums = [...albums];
    try {
      const newAlbums = [...albums];
      newAlbums[albumIndex].photos = newAlbums[albumIndex].photos.filter(p => p !== photoUrl);
      setAlbums(newAlbums);
      await updateProjectAlbums(newAlbums);
      toast.success(t('project_images.photo_removed'));
    } catch (error) {
      setAlbums(previousAlbums);
      toast.error(t('project_images.failed_remove_photo'));
    }
  };

  const handleAlbumSuggestionSelect = (value: string) => {
    if (value === "Custom Album") {
      setNewAlbumName("");
    } else {
      setNewAlbumName(value);
    }
  };

  return (
    <div className="space-y-6">
      {albums.map((album, index) => (
        <Card key={index} className="bg-white/5 border-white/10">
          <CardHeader className="flex flex-row items-center justify-between">
            {editingAlbumIndex === index ? (
              <div className="flex items-center gap-2">
                <Input
                  value={newAlbumName}
                  onChange={(e) => setNewAlbumName(e.target.value)}
                  placeholder={t('project_images.album_name_placeholder')}
                  className="bg-black/20 border-white/10"
                />
                <Button 
                  onClick={() => handleRenameAlbum(index)}
                  variant="secondary"
                  size="sm"
                  disabled={isSaving}
                >
                  {t('common.save_changes')}
                </Button>
                <Button 
                  onClick={() => {
                    setEditingAlbumIndex(null);
                    setNewAlbumName("");
                  }}
                  variant="ghost"
                  size="sm"
                >
                  {t('common.cancel')}
                </Button>
              </div>
            ) : (
              <CardTitle className="text-lg text-white flex items-center gap-2">
                {album.name}
                {canEdit && (
                  <button
                    onClick={() => {
                      setEditingAlbumIndex(index);
                      setNewAlbumName(album.name);
                    }}
                    className="text-white/70 hover:text-white"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                )}
              </CardTitle>
            )}
            {canEdit && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDeleteAlbum(index)}
                className="text-white/70 hover:text-white"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </CardHeader>
          <CardContent>
            <PhotoGallery 
              photos={album.photos} 
              canEdit={canEdit}
              onDeletePhoto={(photoUrl) => handleDeletePhoto(index, photoUrl)}
            />
            {canEdit && (
              <div className="mt-4">
                {isFreeTier ? (
                  <div className="flex items-center justify-center border-2 border-dashed rounded-lg border-white/20 p-6">
                    <div className="text-center">
                      <div className="bg-white/5 p-3 rounded-full">
                        <Lock className="h-6 w-6 text-amber-500" />
                      </div>
                      <span className="text-sm text-white/70">
                        Premium Feature
                      </span>
                      <p className="text-xs text-white/50 max-w-xs">
                        Photo uploads are available on Pro and Producer plans
                      </p>
                      <Button 
                        variant="default" 
                        size="sm" 
                        className="mt-2" 
                        onClick={handleUpgradeClick}
                      >
                        Upgrade Now
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center border-2 border-dashed rounded-lg border-white/20 hover:border-white/40 transition-colors p-6">
                    <div className="text-center">
                      <input
                        type="file"
                        id={`photo-upload-${index}`}
                        className="hidden"
                        accept="image/*"
                        onChange={(e) => handlePhotoUpload(e, index)}
                        disabled={isSaving}
                      />
                      <label
                        htmlFor={`photo-upload-${index}`}
                        className="cursor-pointer flex flex-col items-center gap-2"
                      >
                        <Plus className="h-8 w-8 text-white/70" />
                        <span className="text-sm text-white/70">
                          {isSaving ? t('project_images.uploading') : t('project_images.add_photo')}
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {canEdit && !isAddingAlbum && (
        <Button
          variant="outline"
          className="w-full border-dashed"
          onClick={() => setIsAddingAlbum(true)}
        >
          <Plus className="mr-2 h-4 w-4" />
          {t('project_images.create_album')}
        </Button>
      )}

      {canEdit && isAddingAlbum && (
        <Card className="bg-white/5 border-white/10">
          <CardContent className="pt-6">
            <div className="space-y-4">
              <Select onValueChange={handleAlbumSuggestionSelect}>
                <SelectTrigger className="bg-black/20 border-white/10">
                  <SelectValue placeholder={t('project_images.select_album_type')} />
                </SelectTrigger>
                <SelectContent>
                  {ALBUM_SUGGESTIONS.map((suggestion) => (
                    <SelectItem key={suggestion} value={suggestion}>
                      {suggestion}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex items-center gap-2">
                <Input
                  value={newAlbumName}
                  onChange={(e) => setNewAlbumName(e.target.value)}
                  placeholder={t('project_images.album_name_placeholder')}
                  className="bg-black/20 border-white/10"
                />
                <Button 
                  onClick={handleAddAlbum}
                  variant="secondary"
                  disabled={isSaving}
                >
                  {t('common.add')}
                </Button>
                <Button 
                  onClick={() => {
                    setIsAddingAlbum(false);
                    setNewAlbumName("");
                  }}
                  variant="ghost"
                >
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
